import { Flex, FormLabel, Text } from '@chakra-ui/react'
import { Select } from 'chakra-react-select'
import { useState } from 'react'

import { defaultBorder, thickBoxShadow } from '../chakraTheme'

// Add global styles for dropdown options and control in dark mode
if (typeof document !== 'undefined') {
    const styleId = 'chakra-react-select-dark-mode'
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `
            .dark .chakra-react-select__control,
            .dark div[id*="react-select"][class*="control"] {
                background-color: #1f2937 !important;
                border-color: #374151 !important;
                color: #f3f4f6 !important;
            }
            .dark .chakra-react-select__control:hover,
            .dark div[id*="react-select"][class*="control"]:hover {
                border-color: #374151 !important;
            }
            .dark .chakra-react-select__single-value,
            .dark div[id*="react-select"][class*="single-value"] {
                color: #f3f4f6 !important;
            }
            .dark .chakra-react-select__input-container input,
            .dark div[id*="react-select"] input {
                color: #f3f4f6 !important;
            }
            .dark .chakra-react-select__placeholder,
            .dark div[id*="react-select"][class*="placeholder"] {
                color: #9ca3af !important;
            }
            .dark div[role="option"] {
                background-color: #1f2937 !important;
                color: #f3f4f6 !important;
                border-bottom: 1px solid #374151 !important;
            }
            .dark div[role="option"]:hover {
                background-color: #374151 !important;
            }
            .dark div[role="option"][aria-selected="true"] {
                background-color: #374151 !important;
                font-weight: bold !important;
            }
        `
        document.head.appendChild(style)
    }
}

const FormikSelectInput = ({
    options,
    field,
    form,
    label,
    isRequired,
    isDisabled,
    height,
    width,
    labelColor,
    starColor,
    menuListProps,
    sx,
    emptySearchMessage,
    ...rest
}) => {
    const [isSearchEmpty, setIsSearchEmpty] = useState(false)
    return (
        <Flex
            width={'full'}
            flexDirection="column"
            className="dark:text-gray-100"
        >
            {label && (
                <FormLabel
                    display={'flex'}
                    color={isDisabled ? '#adadad' : labelColor || 'initial'}
                    className="dark:text-gray-100"
                >
                    {label}
                    {isRequired && (
                        <Text
                            color={
                                isDisabled
                                    ? '#f5b2b2'
                                    : starColor || 'pomegranate'
                            }
                            ml={1}
                            className="dark:text-[#cb3434]"
                        >
                            *
                        </Text>
                    )}
                </FormLabel>
            )}
            <div className="[&_.chakra-react-select__control]:dark:bg-gray-800 [&_.chakra-react-select__control]:dark:border-gray-700 [&_.chakra-react-select__control]:dark:text-gray-100 [&_.chakra-react-select__single-value]:dark:text-gray-100 [&_.chakra-react-select__placeholder]:dark:text-gray-400 [&_.chakra-react-select__input]:dark:text-gray-100 [&_.chakra-react-select__input-container]:dark:text-gray-100 [&_.chakra-react-select__menu]:dark:bg-gray-800 [&_.chakra-react-select__menu-list]:dark:bg-gray-800 [&_div[role='option']]:dark:bg-gray-800 [&_div[role='option']]:dark:text-gray-100 [&_div[role='option']:hover]:dark:bg-gray-700 [&_div[role='option'][aria-selected='true']]:dark:bg-gray-700 [&_div[role='option'][aria-selected='true']]:dark:font-bold">
                <Select
                    inputId={field.name}
                    key={field.name}
                    options={options}
                    name={field.name}
                    value={
                        options
                            ? options.find(
                                  option => option.value === field.value
                              )
                            : ''
                    }
                    onChange={option =>
                        rest.isMulti
                            ? form.setFieldValue(field.name, option)
                            : form.setFieldValue(field.name, option.value)
                    }
                    onBlur={field.onBlur}
                    focusBorderColor="none"
                    useBasicStyles
                    className="dark:text-gray-100"
                    chakraStyles={{
                        control: (provided, state) => {
                            const isDark =
                                document.documentElement.classList.contains(
                                    'dark'
                                )
                            return {
                                ...provided,
                                boxShadow: 'none',
                                borderRadius: 0,
                                borderColor: isDark ? '#374151' : 'mineshaft',
                                border: defaultBorder,
                                _hover: {
                                    borderColor: isDark
                                        ? '#374151'
                                        : 'mineshaft',
                                },
                                _focusVisible: {
                                    boxShadow: '0 0 0 2px #3182ce',
                                },
                                height: height || 14,
                                width: width || 'full',
                                cursor: 'pointer',
                                _disabled: {
                                    opacity: 0.4,
                                },
                                bgColor: isDark ? '#1f2937' : 'tablebg.100',
                                color: isDark ? '#f3f4f6' : 'inherit',
                            }
                        },
                        valueContainer: provided => ({
                            ...provided,
                            padding: '0 0 0 16px',
                            paddingInlineStart: '16px',
                        }),
                        singleValue: provided => ({
                            ...provided,
                            margin: 0,
                            textOverflow: 'unset',
                            color: 'inherit',
                            className: 'dark:text-gray-100',
                        }),
                        dropdownIndicator: (provided, { selectProps }) => ({
                            ...provided,
                            marginLeft: 2,
                            marginRight: 4,
                            height: 5,
                            width: 5,
                            '> svg': {
                                transform: `rotate(${
                                    selectProps.menuIsOpen ? -180 : 0
                                }deg)`,
                            },
                        }),
                        menu: provided => ({
                            ...provided,
                            width: width || 'full',
                            zIndex: 9999,
                            className: 'dark:bg-gray-800',
                        }),
                        menuPortal: provided => ({
                            ...provided,
                            zIndex: 9999,
                        }),
                        menuList: provided => ({
                            ...provided,
                            minW: 0,
                            maxW: { md: 420 },
                            w: 'calc(100% - 5px)',
                            borderRadius: 0,
                            border: '3px solid #333333',
                            py: 0,
                            boxShadow: thickBoxShadow,
                            bgColor: 'white',
                            className: 'dark:bg-gray-800 dark:border-gray-700',
                            ...(sx || {}),
                            ...(menuListProps || {}),
                            ...(isSearchEmpty && {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }),
                        }),
                        option: (provided, state) => {
                            return {
                                ...provided,
                                padding: {
                                    base: '1rem 1.5rem',
                                    md: '1rem 1.125rem',
                                },
                                _hover: {
                                    backgroundColor: 'athensgray !important',
                                },
                                backgroundColor: 'white !important',
                                color: '#333333 !important',
                                fontWeight: state.isSelected
                                    ? 'bold'
                                    : 'normal',
                                fontSize: 'sm',
                                borderBottom: '1px solid #e3e3e3',
                            }
                        },
                        placeholder: provided => {
                            return {
                                ...provided,
                                color: 'silver2',
                                className: 'dark:text-gray-400',
                            }
                        },
                    }}
                    isDisabled={isDisabled}
                    noOptionsMessage={() => {
                        if (emptySearchMessage) {
                            setIsSearchEmpty(true)
                        }
                        return emptySearchMessage || 'No options available'
                    }}
                    onInputChange={() => setIsSearchEmpty(false)}
                    onMenuOpen={() => setIsSearchEmpty(false)}
                    {...rest}
                />
            </div>
            {form.touched[field.name] && form.errors[field.name] && (
                <Text
                    color={starColor || 'pomegranate'}
                    mt={2}
                    fontSize={'sm'}
                    className="dark:text-[#cb3434]"
                >
                    {form.errors[field.name]}
                </Text>
            )}
        </Flex>
    )
}

export default FormikSelectInput
